'use client';

/**
 * Embedded onboarding assistant for the no-site page.
 *
 * Shown to a logged-in (email-confirmed) user who has no site yet. Unlike the
 * floating site ChatAssistant, this is a first-class, always-open panel: it's the
 * obvious next step on the page. It talks to `POST /api/assistant/onboarding-chat`
 * (no site_id — the user has no site), which runs on DeepSeek and helps the user
 * prepare for onboarding: what Powston needs, connecting their brand's cloud, and
 * getting their home network ready for a controller.
 *
 * It pre-seeds context from the user's signup answers (brand / decision) so it
 * opens already knowing their situation, and offers brand-aware starter prompts.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Send, Bot, ThumbsUp, ThumbsDown } from 'lucide-react';
import { mdToHtml } from '@/app/lib/miniMarkdown';
import { recoverPersistedAssistantTurn } from '@/app/lib/assistantStreamRecovery';

type Role = 'user' | 'assistant';
type Rating = 'good' | 'bad' | null;
interface Msg { role: Role; content: string; id?: number; feedback?: Rating; }

interface SignupMeta {
  brand?: string;
  decision?: 'proceed' | 'upgrade' | 'exit';
  inverter_serial?: string;
  phase?: 'single' | 'three';
}

interface Props {
  signupMeta?: SignupMeta;
  userName?: string;
}

// Brand-aware starter prompts so the user isn't staring at a blank box.
function starterPrompts(meta?: SignupMeta): string[] {
  const brand = (meta?.brand || '').trim();
  const base = brand
    ? [`What do I need to get my ${brand} system working with Powston?`]
    : ['What do I need to get started with Powston?'];
  return [
    ...base,
    'How do I get my home network ready for a Powston controller?',
    'How do I connect my inverter brand to Powston?',
  ];
}

// A short, non-PII context hint so the backend tailors the first reply to the
// user's situation without them having to repeat what they told the survey.
function pageContextFor(meta?: SignupMeta): string {
  const bits = ['No-site onboarding page'];
  if (meta?.brand) bits.push(`brand=${meta.brand}`);
  if (meta?.decision) bits.push(`survey decision=${meta.decision}`);
  return bits.join('; ');
}

export default function OnboardingAssistant({ signupMeta, userName }: Props) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [toolHint, setToolHint] = useState<string>('');
  const [conversationId, setConversationId] = useState<number | null>(null);
  // Shown when the assistant can't continue for a reason the user can't self-fix
  // (free limit reached, or a backend AI-service issue). Onboarding is pinned to
  // DeepSeek and ignores user keys, so we point to support rather than "add a key".
  const [helpHint, setHelpHint] = useState(false);
  const restored = useRef(false);
  // Set the moment the user sends/clicks a prompt, so a slow history-restore
  // can't replace their live conversation out from under them.
  const started = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, toolHint]);

  // Restore the most recent onboarding conversation (if any) so history persists
  // across reloads. Best-effort — ignore failures.
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    (async () => {
      try {
        const listRes = await fetch('/api/assistant/onboarding/conversations');
        if (!listRes.ok) return;
        const { conversations } = await listRes.json();
        const latest = conversations?.[0];
        if (!latest?.conversation_id) return;
        const convRes = await fetch(`/api/assistant/onboarding/conversation/${latest.conversation_id}`);
        if (!convRes.ok) return;
        const data = await convRes.json();
        // The user may have started a new conversation while this was in flight —
        // don't clobber their live messages / conversation id.
        if (started.current) return;
        setConversationId(data.conversation_id);
        setMessages((data.messages || []).map((m: Msg) => ({
          role: m.role, content: m.content, id: m.id, feedback: m.feedback ?? null,
        })));
      } catch { /* no history is fine */ }
    })();
  }, []);

  async function send(textArg?: string) {
    const text = (textArg ?? input).trim();
    if (!text || busy) return;
    started.current = true;
    setInput('');
    setToolHint('');
    setHelpHint(false);
    const knownMessageIds = messages.flatMap((message) => message.id == null ? [] : [message.id]);
    setMessages((prev) => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setBusy(true);
    let activeConversationId = conversationId;
    let sawDone = false;

    try {
      const res = await fetch('/api/assistant/onboarding-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          conversation_id: conversationId,
          page_context: pageContextFor(signupMeta),
        }),
      });
      if (!res.ok || !res.body) {
        appendToLast(`Something went wrong (${res.status}).`);
        return;
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let i;
        while ((i = buf.indexOf('\n\n')) >= 0) {
          const line = buf.slice(0, i).trim();
          buf = buf.slice(i + 2);
          if (!line.startsWith('data:')) continue;
          let ev: { type: string; text?: string; name?: string; message?: string; reason?: string; conversation_id?: number; message_id?: number };
          try { ev = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (ev.type === 'meta' && ev.conversation_id) {
            activeConversationId = ev.conversation_id;
            setConversationId(ev.conversation_id);
          }
          else if (ev.type === 'text' && ev.text) { setToolHint(''); appendToLast(ev.text); }
          else if (ev.type === 'tool') setToolHint(`Looking up ${prettyTool(ev.name)}…`);
          else if (ev.type === 'saved' && ev.message_id) setIdOnLast(ev.message_id);
          else if (ev.type === 'error' && ev.message) {
            appendToLast(` ${ev.message}`);
            if (ev.reason === 'onboarding_quota' || ev.reason === 'usage_limit' || ev.reason === 'auth' || ev.reason === 'quota') setHelpHint(true);
          }
          else if (ev.type === 'done') { sawDone = true; setToolHint(''); }
        }
      }
      // The backend ends every turn (including handled errors) with a `done`
      // event, so a clean EOF without one is a broken stream: reconcile from
      // persisted history instead of presenting the partial answer as final.
      if (!sawDone) throw new Error('stream ended before done');
    } catch {
      const recovered = await recoverPersistedAssistantTurn({
        conversationId: activeConversationId,
        conversationPath: (id) => `/api/assistant/onboarding/conversation/${id}`,
        userMessage: text,
        knownMessageIds,
      });
      if (recovered) {
        setConversationId(recovered.conversation_id);
        setMessages(recovered.messages.map((message) => ({
          role: message.role,
          content: message.content,
          id: message.id,
          feedback: message.feedback ?? null,
        })));
      } else {
        appendToLast(' Sorry — the assistant is unavailable right now. Please try again.');
      }
    } finally {
      setBusy(false);
      setToolHint('');
    }
  }

  function appendToLast(chunk: string) {
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.role === 'assistant') next[next.length - 1] = { ...last, content: last.content + chunk };
      return next;
    });
  }

  function setIdOnLast(id: number) {
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.role === 'assistant') next[next.length - 1] = { ...last, id };
      return next;
    });
  }

  async function rate(idx: number, rating: 'good' | 'bad') {
    const m = messages[idx];
    if (!m?.id) return;
    const next: Rating = m.feedback === rating ? null : rating;
    setMessages((prev) => prev.map((x, i) => (i === idx ? { ...x, feedback: next } : x)));
    try {
      await fetch(`/api/assistant/message/${m.id}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating: next ?? 'clear' }),
      });
    } catch { /* keep the optimistic state; it'll reconcile on next load */ }
  }

  const prompts = starterPrompts(signupMeta);
  const greeting = userName ? `Hi ${userName.split(' ')[0]} — let's get you set up.` : "Let's get you set up.";

  return (
    <div className="rounded-lg border border-border bg-card shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border font-semibold text-sm">
        <Bot size={18} className="text-blue-600 dark:text-blue-400" />
        Onboarding assistant
      </div>

      {/* Always-visible expectation-setter: this is requirements-gathering, not
          advice or a final answer — Powston verifies and follows up if they proceed. */}
      <div className="px-4 py-2 text-[11px] leading-snug text-slate-500 dark:text-gray-400 border-b border-border bg-slate-50 dark:bg-slate-800/40">
        This chat is here to understand your setup and gather your requirements — it&apos;s general
        information, not financial or electrical advice, and not a final answer on what your system
        can do. If you decide to proceed, the Powston team will review your details and get back to
        you on what&apos;s possible for your specific site.
      </div>

      <div ref={scrollRef} className="max-h-[420px] min-h-[180px] overflow-y-auto px-4 py-3 space-y-3 text-sm">
        {messages.length === 0 && (
          <div className="text-slate-500 dark:text-gray-400 space-y-3">
            <p>{greeting} I can help you prepare for what you'll need, connect your inverter brand, and get your network ready for a Powston controller.</p>
            <div className="flex flex-col gap-2">
              {prompts.map((p) => (
                <button
                  key={p}
                  onClick={() => send(p)}
                  disabled={busy}
                  className="text-left rounded-lg border border-border px-3 py-2 hover:border-foreground/30 transition-colors disabled:opacity-50"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, idx) => (
          <div key={idx} className={m.role === 'user' ? 'text-right' : ''}>
            {m.role === 'assistant' ? (
              <span className="inline-block max-w-[90%] rounded-lg px-3 py-2 bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-gray-100 text-left">
                {m.content
                  ? <span className="md-body" dangerouslySetInnerHTML={{ __html: mdToHtml(m.content) }} />
                  : (busy ? '…' : '')}
              </span>
            ) : (
              <span className="inline-block max-w-[90%] whitespace-pre-wrap rounded-lg px-3 py-2 bg-blue-600 text-white">
                {m.content}
              </span>
            )}
            {m.role === 'assistant' && m.id && m.content && (
              <div className="mt-1 flex gap-2 text-slate-400">
                <button onClick={() => rate(idx, 'good')} aria-label="Helpful"
                  className={'hover:text-green-600 ' + (m.feedback === 'good' ? 'text-green-600' : '')}>
                  <ThumbsUp size={14} />
                </button>
                <button onClick={() => rate(idx, 'bad')} aria-label="Not helpful"
                  className={'hover:text-red-600 ' + (m.feedback === 'bad' ? 'text-red-600' : '')}>
                  <ThumbsDown size={14} />
                </button>
              </div>
            )}
          </div>
        ))}
        {toolHint && <div className="text-xs text-slate-400 italic">{toolHint}</div>}
      </div>

      {helpHint && (
        <div className="flex items-center justify-between gap-2 px-4 py-2 text-xs border-t border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300">
          <span>Your Powston team can still help you finish setting up.</span>
          <a href="mailto:service@powston.com" className="shrink-0 font-medium underline hover:no-underline">Email support →</a>
        </div>
      )}

      <div className="flex items-end gap-2 p-3 border-t border-border">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={1}
          placeholder="Ask about getting set up…"
          className="flex-1 resize-none rounded-lg border border-slate-300 dark:border-slate-600 bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={() => send()}
          disabled={busy || !input.trim()}
          aria-label="Send"
          className="rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white p-2"
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
}

function prettyTool(name?: string): string {
  switch (name) {
    case 'get_brand_setup_steps': return 'your brand setup steps';
    case 'save_onboarding_details': return 'your details';
    case 'get_network_readiness_checklist': return 'the network checklist';
    case 'request_brand_cloud_connect': return 'how to connect your brand';
    default: return 'setup info';
  }
}
